<template>
  <f-modal
    :class="{ 'is-visible': isVisible, 'is-on-top': true }"
    @close="handleClose">
    <f-modal-header>
      <h3 class="heading-medium">
        <template v-if="editMode">
          {{ $t('calendar.editMeeting') }}
        </template>
        <template v-else>
          {{ $t('calendar.addMeetings') }}
        </template>
      </h3>
    </f-modal-header>

    <f-modal-body>
      <validation-observer
        ref="calendar-meeting-popup-form"
        tag="form">

        <div class="flex full-width">
          <div class="form-group full-width mr-2">
            <label for="start-date">{{ $t('adviserSettingsEducationInfo.timeStart') }}</label>
            <validation-provider
              rules="required"
              name="start-date"
              v-slot="{ errors }">
              <div
                :class="{
                  'is-date-range': true,
                  'vs-wrap-text': true,
                  'is-invalid': errors.length
                }">
                <v-date-picker
                  v-model="data.start_date"
                  mode="date"
                  :locale="$i18n.locale"
                  class="date"
                  :min-date='new Date()'
                  :max-date='data.end_date'>
                  <template v-slot="{ inputValue, togglePopover }">
                    <input
                      :value="inputValue"
                      @click="togglePopover()"
                      class="form-control f-input" />
                  </template>
                </v-date-picker>
                <div class="invalid-feedback">
                  {{ errors[0] }}
                </div>
              </div>
            </validation-provider>
          </div>

          <div class="form-group full-width">
            <label for="start-time">{{ $t('calendar.startTime') }}</label>
            <validation-provider
              :rules="data.all_day ? '' : 'required'"
              name="start-time"
              v-slot="{ errors }">
              <div
                :class="{
                  'is-date-range': true,
                  'vs-wrap-text': true,
                  'is-invalid': errors.length,
                  'dont-show-date': true
                }">
                <v-date-picker
                  v-model="data.start_time"
                  mode="time"
                  :locale="$i18n.locale"
                  is24hr
                  class="date"
                  :min-date='new Date()'>
                  <template v-slot="{ inputValue, togglePopover }">
                    <input
                      :value="inputValue"
                      :disabled="data.all_day || !data.start_date"
                      @click="togglePopover()"
                      class="form-control f-input" />
                  </template>
                </v-date-picker>
                <div class="invalid-feedback">
                  {{ errors[0] }}
                </div>
              </div>
            </validation-provider>
          </div>
        </div>

        <div class="flex full-width">
          <div class="form-group mr-2 full-width">
            <label for="end-date">{{ $t('adviserSettingsEducationInfo.timeEnd') }}</label>
            <validation-provider
              rules="required"
              name="end-date"
              v-slot="{ errors }">
              <div
                :class="{
                  'is-date-range': true,
                  'vs-wrap-text': true,
                  'is-invalid': errors.length
                }">
              <v-date-picker
                v-model="data.end_date"
                mode="date"
                :locale="$i18n.locale"
                class="date"
                :min-date='data.start_date'>
                <template v-slot="{ inputValue, togglePopover }">
                  <div class="flex justify-center items-center">
                    <input
                      :value="inputValue"
                      @click="togglePopover()"
                      class="form-control f-input" />
                  </div>
                </template>
              </v-date-picker>
              <div class="invalid-feedback">
                  {{ errors[0] }}
                </div>
              </div>
            </validation-provider>
          </div>

          <div class="form-group full-width">
            <label for="end-time">{{ $t('calendar.endTime') }}</label>
            <validation-provider
              :rules="data.all_day ? '' : 'required'"
              name="end-time"
              v-slot="{ errors }">
              <div
                :class="{
                  'is-date-range': true,
                  'vs-wrap-text': true,
                  'is-invalid': errors.length,
                  'dont-show-date': true
                }">
                <v-date-picker
                  v-model="data.end_time"
                  mode="time"
                  :locale="$i18n.locale"
                  is24hr
                  class="date">
                  <template v-slot="{ inputValue, togglePopover }">
                    <input
                      :value="inputValue"
                      :disabled="data.all_day || !data.end_date"
                      @click="togglePopover()"
                      class="form-control f-input" />
                  </template>
                </v-date-picker>
                <div class="invalid-feedback">
                  {{ errors[0] }}
                </div>
              </div>
            </validation-provider>
          </div>
        </div>

        <div class="form-group">
          <label for="all-day">{{ $t('calendar.allSayEvent') }}</label>

          <validation-provider
            rules="required"
            name="all-day"
            v-slot="{ errors }">
            <div class="flex">
              <f-form-radio
                class="half-width mr-2"
                :value="true"
                v-model="data.all_day">
                {{ $t('ui.yes') }}
              </f-form-radio>
              <f-form-radio
                class="half-width"
                :value="false"
                v-model="data.all_day">
                {{ $t('ui.no') }}
              </f-form-radio>
            </div>
            <div class="invalid-feedback">
              {{ errors[0] }}
            </div>
          </validation-provider>
        </div>

        <div class="form-group">
          <label for="customer">{{ $t('client.client') }}</label>
          <v-select
            name="customer"
            :options="allCustomers"
            v-model="data.customer_id"
            :reduce="(customer) => customer.customer_id"
            :placeholder="$t('messagesList.chooseCustomer')"
            :searchable="true"
            label="name">
            <template slot="no-options">
              {{ $t('ui.noResultsForGivenPhrase') }}
            </template>
            <template slot="option" slot-scope="option">
              {{ option.name }} {{ option.surname }}
            </template>
            <template slot="selected-option" slot-scope="option">
              {{ option.name }} {{ option.surname }}
            </template>
          </v-select>
        </div>

        <div
          v-if="data.customer_id"
          class="form-group">
          <label for="customer-description">{{ $t('notesList.noteForClient') }}</label>

          <validation-provider
            name="customer-description"
            v-slot="{ errors }">
            <f-textarea
              v-model="data.customer_description"
              :rows="6"
              :state="errors.length ? 'invalid' : null"/>
            <div class="invalid-feedback">
              {{ errors[0] }}
            </div>
          </validation-provider>
        </div>

        <div class="form-group">
          <label for="description">{{ $t('notesList.note') }}</label>

          <validation-provider
            name="description"
            v-slot="{ errors }">
            <f-textarea
              v-model="data.description"
              :rows="6"
              :state="errors.length ? 'invalid' : null"/>
            <div class="invalid-feedback">
              {{ errors[0] }}
            </div>
          </validation-provider>
        </div>
      </validation-observer>

      <div class="full-wdth text-right">
        <f-button
          v-if="editMode"
          theme="dark"
          @click.prevent="downloadICalFile">
          {{ $t('messageForm.downloadFile') }} iCal
        </f-button>
      </div>
    </f-modal-body>
    <f-modal-footer>
      <div class="flex flex-justify-content-between">
        <f-button
          class="ml-0"
          theme="danger"
          @click.prevent="handleClose">
          {{ $t('ui.cancel') }}
        </f-button>
        <f-button
          class="ml-auto"
          theme="primary"
          :disabled="submitInProgress"
          @click.prevent="validateForm">
          <div
            v-if="submitInProgress"
            class="loading-spinner"
            role="status">
          </div>
          <template v-if="editMode">{{ $t('ui.save') }}</template>
          <template v-else>{{ $t('ui.add') }}</template>
        </f-button>
      </div>
    </f-modal-footer>
  </f-modal>
</template>
<script>
import Vue from 'vue';
import FormUtils from '@/utils/FormUtils.js';

export default {
  name: 'calendar-meeting-popup',
  props: {
    allCustomers: {
      type: Array,
      required: true
    }
  },
  watch: {
    'data.start_date': function (newValue) {
      if (this.isVisible && (newValue && newValue > this.data.end_date)) {
        this.data.end_date = new Date(newValue);
      }
    },
    'data.end_date': function (newValue) {
      if (newValue && newValue < this.data.start_date) {
        this.data.start_date = new Date(newValue);
      }
    },
    'data.start_time': function (newValue) {
      if ((this.isSameDay(this.data.start_date, this.data.end_date)) && newValue && (!this.data.end_time || (newValue > this.data.end_time))) {
        this.data.end_time = new Date(newValue);
        this.data.end_time.setTime(this.data.end_time.getTime() + (1 * 60 * 60 * 1000));
      }
    },
    'data.end_time': function (newValue) {
      if ((this.data.start_date >= this.data.end_date) && newValue && (!this.data.start_time || (newValue < this.data.start_time))) {
        this.data.start_time = new Date(newValue);
        this.data.start_time.setTime(this.data.start_time.getTime() - (1 * 60 * 60 * 1000));
      }
    },
    'data.customer_id': function (newValue) {
      if (!newValue) {
        this.data.customer_description = '';
      }
    }
  },
  data() {
    return {
      data: {
        id: 0,
        start_date: null,
        start_time: null,
        end_date: null,
        end_time: null,
        all_day: false,
        description: null,
        customer_id: 0,
        customer_description: ''
      },
      editMode: false,
      isVisible: false,
      submitInProgress: false
    };
  },
  mounted() {
    this.$bus.$on('calendar-add-meeting-popup-show', this.openPopup);
  },
  methods: {
    handleClose() {
      document.body.classList.remove('no-scroll');
      this.$bus.$emit('calendar-meeting-popup-closed');
      this.isVisible = false;
    },
    openPopup(meeting = null, defaultDate = null) {
      this.editMode = !!meeting;

      if (this.editMode) {
        this.data.id = meeting.id;
        this.data.start_date = new Date(meeting.start_date);
        this.data.start_time = null;
        this.data.end_date = new Date(meeting.end_date);
        this.data.end_time = null;
        this.data.all_day = !!meeting.all_day;
        this.data.description = meeting.description;
        this.data.customer_id = meeting.customer_id;
        this.data.customer_description = meeting.customer_description;

        if (meeting.start_time) {
          this.data.start_time = new Date(meeting.start_date + ' ' + meeting.start_time);
        }

        if (meeting.end_time) {
          this.data.end_time = new Date(meeting.end_date + ' ' + meeting.end_time);
        }
      } else {
        let currentDate = new Date();
        if (defaultDate) {
          currentDate = new Date(defaultDate);
        }

        this.data.id = 0;
        this.data.start_date = currentDate;
        this.data.start_time = null;
        this.data.end_date = currentDate;
        this.data.end_time = null;
        this.data.all_day = false;
        this.data.description = null;
        this.data.customer_id = 0;
        this.data.customer_description = '';
      }

      setTimeout(() => {
        this.isVisible = true;
        this.submitInProgress = false;
        document.body.classList.add('no-scroll');

        if (this.$refs['calendar-meeting-popup-form']) {
          this.$refs['calendar-meeting-popup-form'].reset();
        }
      }, 0);
    },
    validateForm () {
      FormUtils.validate(this.$refs['calendar-meeting-popup-form'], this.submitForm);
    },
    submitForm () {
      this.submitInProgress = true;
      let formDataToSend = this.prepareFormDataToSend();
      let endpointUrl = this.editMode ? '/api/adviser/meetings/update/' + this.data.id : '/api/adviser/meetings/store';

      FormUtils.submit(this, {
        endpoint: endpointUrl,
        formData: formDataToSend,
        successTitle: this.$t('calendar.savedMeeting'),
        successText: this.$t('calendar.selectedMeetingWasRecorded'),
        errorTitle: this.$t('ui.errorOccurred'),
        errorText: this.$t('calendar.meetingModifyError'),
        successAction: () => {
          this.submitInProgress = false;
          this.$bus.$emit('calendar-refresh');
          this.handleClose();
        },
        failAction: () => {
          this.submitInProgress = false;
        }
      });
    },
    prepareFormDataToSend () {
      let formDataToSend = {
        start_date: this.prepareDateString(this.data.start_date),
        start_time: this.prepareTimeString(this.data.start_time),
        end_date: this.prepareDateString(this.data.end_date),
        end_time: this.prepareTimeString(this.data.end_time),
        all_day: this.data.all_day ? 1 : 0,
        description: this.data.description,
        customer_id: this.data.customer_id ? this.data.customer_id : 0,
        customer_description: this.data.customer_description
      };

      return formDataToSend;
    },
    prepareDateString (input) {
      if (input === null) {
        return null;
      }
      input = new Date(input);

      return [
        input.getFullYear(),
        input.getMonth() + 1,
        input.getDate()
      ]
        .map(n => n < 10 ? '0' + n : n)
        .join('-');
    },
    prepareTimeString (input) {
      if (input === null) {
        return null;
      }
      input = new Date(input);

      return [
        input.getHours(),
        input.getMinutes()
      ]
        .map(n => n < 10 ? '0' + n : n)
        .join(':');
    },
    isSameDay (dateOne, dateTwo) {
      dateOne = new Date(dateOne);
      dateTwo = new Date(dateTwo);
      if ((dateOne.getDate() === dateTwo.getDate()) && (dateOne.getMonth() === dateTwo.getMonth()) && (dateOne.getFullYear() === dateTwo.getFullYear())) {
        return true;
      }

      return false;
    },
    downloadICalFile () {
      this.submitInProgress = true;
      let endpointUrl = '/api/adviser/meetings/download-i-calendar/' + this.data.id;
      let fileName = [this.prepareDateString(this.data.start_date), 'wydarzenie', this.data.id + '.ics'].join('-');

      this.$http.get(endpointUrl, {
        responseType: 'blob',
      }).then(response => {
        if (response.status === 200) {
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', fileName);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          this.submitInProgress = false;
        } else {
          Vue.swal({
            title: this.$t('inquiryFiles.downloadFileError'),
            html: this.$t('ui.tryAgain'),
            icon: 'warning',
            confirmButtonText: 'OK',
            buttonsStyling: true
          });
          this.submitInProgress = false;
        }
      }).catch(error => {
        console.log(error);
        Vue.swal({
          title: this.$t('inquiryFiles.downloadFileError'),
          html: this.$t('ui.tryAgain'),
          icon: 'warning',
          confirmButtonText: 'OK',
          buttonsStyling: true
        });
        this.submitInProgress = false;
      });
    }
  },
  beforeDestroy () {
    this.$bus.$off('calendar-add-meeting-popup-show', this.openPopup);
  }
}
</script>
